import * as React from 'react';
import { cn } from '@/lib/cn';

export type BannerVariant = 'success' | 'error' | 'warning' | 'info';

export interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Variante visual — define color de fondo, borde y texto. */
    variant: BannerVariant;
    /** Título opcional en negrita arriba del mensaje. */
    title?: string;
    /** Si true, muestra botón X que dispara `onDismiss`. */
    dismissible?: boolean;
    /** Callback al cerrar (si dismissible=true). */
    onDismiss?: () => void;
}

/**
 * Banner inline para mensajes contextuales atados a una pantalla/form.
 *
 * Patrón de uso:
 *  - `error`: errores de validación / API que el user debe leer (login fallido,
 *    OTP inválido, etc.). Persiste hasta que el user haga otra acción.
 *  - `success`: confirmación de acción exitosa (sin auto-dismiss — el user lee
 *    a su ritmo y avanza).
 *  - `warning`: estados intermedios o advertencias (sesión por expirar, etc.).
 *  - `info`: feedback informativo neutral.
 *
 * Para feedback transitorio cross-page (logout exitoso, copiado, etc.) usa toast
 * (lib `sonner` ya disponible). Banner = bound a form/pantalla.
 */
// DEC-021: tokens semánticos de estado (success/warning/info) + destructive (error), con el
// patrón suave borde/10 + texto del token. Antes eran colores crudos de Tailwind (green/red/amber/blue).
const variantStyles: Record<BannerVariant, { container: string; icon: string }> = {
    success: {
        container: 'border-success/30 bg-success/10 text-success',
        icon: 'text-success',
    },
    error: {
        container: 'border-destructive/30 bg-destructive/10 text-destructive',
        icon: 'text-destructive',
    },
    warning: {
        container: 'border-warning/30 bg-warning/10 text-warning',
        icon: 'text-warning',
    },
    info: {
        container: 'border-info/30 bg-info/10 text-info',
        icon: 'text-info',
    },
};

function BannerIcon({ variant, className }: { variant: BannerVariant; className?: string }) {
    const common = cn('h-5 w-5 shrink-0', className);
    if (variant === 'success') {
        return (
            <svg className={common} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                />
            </svg>
        );
    }
    if (variant === 'error') {
        return (
            <svg className={common} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                />
            </svg>
        );
    }
    if (variant === 'warning') {
        return (
            <svg className={common} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                    fillRule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 8a1 1 0 100 2 1 1 0 000-2z"
                    clipRule="evenodd"
                />
            </svg>
        );
    }
    // info
    return (
        <svg className={common} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
            />
        </svg>
    );
}

export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
    ({ variant, title, children, dismissible, onDismiss, className, ...rest }, ref) => {
        const styles = variantStyles[variant];
        return (
            <div
                ref={ref}
                role={variant === 'error' ? 'alert' : 'status'}
                aria-live={variant === 'error' ? 'assertive' : 'polite'}
                className={cn(
                    'flex items-start gap-3 rounded-md border px-3 py-2.5 text-sm',
                    styles.container,
                    className,
                )}
                {...rest}
            >
                <BannerIcon variant={variant} className={styles.icon} />
                <div className="flex-1 min-w-0">
                    {title && <p className="font-semibold leading-tight">{title}</p>}
                    {children && <div className={cn(title && 'mt-0.5')}>{children}</div>}
                </div>
                {dismissible && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        aria-label="Cerrar"
                        className={cn(
                            'rounded p-0.5 transition-opacity opacity-60 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                            styles.icon,
                        )}
                    >
                        <svg
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                        </svg>
                    </button>
                )}
            </div>
        );
    },
);
Banner.displayName = 'Banner';
